import React, { useState, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { AccordionStackable } from 'ui/AccordionStackable/AccordionStackable';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { LoadingBar, ErrorBar } from 'ui/NetworkStatusBar';
import { makeBookingManagerApi } from 'services/BookingManagerApi/BookingManagerApi';
import { IHeadlineLineItemBreakdownResponse, IHeadlineLineItemBreakdown } from 'services/BookingManagerApi/types/HeadlineLineItemBreakdown';
import { IGuestInformationResponse } from 'services/BookingManagerApi/types/GuestInformationResponse';
import { usePurchaseOrderDetailQuery } from './hooks/usePurchaseOrderDetailQuery';
import { IPurchaseOrderSearchLineItem } from 'services/BookingManagerApi/types/PurchaseOrdersSearchResponse';
import { PurchaseOrderItemsTable } from './components/PurchaseOrderItemsTable';
import { PurchaseOrderInfoPanel } from './components/PurchaseOrderInfoPanel';
import { BorderWithLabel } from 'ui/BorderWithLabel/BorderWithLabel';
import { TCurrencyCode } from 'interfaces';
import { InvoiceSection } from './components/InvoiceSection';
import { InvoicePreviewPanel } from './components/InvoicePreviewPanel';
import { UploadInvoiceModal } from './components/UploadInvoiceModal';
import { LeaveWithoutSavingModal } from 'ui/LeaveWithoutSavingModal';

const formatDate = (dateString: string): string => format(parseISO(dateString), 'dd MMM yy');
const formatDateTime = (dateString: string): string => format(parseISO(dateString), 'dd MMMM yyyy, HH:mm');
const formatAmount = (amount: number): string =>
  new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

const getAllBreakdownItemTitles = (breakdown: IHeadlineLineItemBreakdown): Map<string, string> => {
  const map = new Map<string, string>();
  const addItems = (items: Array<{ uuid: string; title: string }>) =>
    items.forEach(item => map.set(item.uuid, item.title));

  addItems(breakdown.Accommodation?.items ?? []);
  addItems(breakdown.Transfer?.items ?? []);
  addItems(breakdown['Ground Service']?.items ?? []);
  addItems(breakdown.Supplement?.items ?? []);
  addItems(breakdown.Fine?.items ?? []);
  addItems(breakdown.Bespoke?.items ?? []);
  return map;
};

export const PurchaseOrderDetailPage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const bookingUuid = searchParams.get('bookingUuid') ?? '';
  const purchaseOrderCode = searchParams.get('purchaseOrderCode') ?? undefined;

  const { data: po, isLoading: poLoading, isError: poError } = usePurchaseOrderDetailQuery(bookingUuid, purchaseOrderCode);

  const { data: guestData, isLoading: guestLoading } = useQuery<IGuestInformationResponse>({
    queryKey: ['guestInformation', bookingUuid],
    queryFn: async () => {
      const response = await makeBookingManagerApi().getGuestInformation(bookingUuid);
      return (response.data as any).guests ?? response.data;
    },
    enabled: !!bookingUuid,
  });

  const { data: breakdownData, isLoading: breakdownLoading } = useQuery<IHeadlineLineItemBreakdownResponse>({
    queryKey: ['headlineBreakdown', bookingUuid],
    queryFn: async () => {
      const response = await makeBookingManagerApi().getHeadlineLineItemBreakdown(bookingUuid);
      return response.data;
    },
    enabled: !!bookingUuid,
  });

  const isLoading = poLoading || guestLoading || breakdownLoading;
  const [isPurchaseOrderOpen, setIsPurchaseOrderOpen] = useState(true);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);

  const invoiceFilePreviewUrl = useMemo(() => {
    if (!invoiceFile) return null;
    return URL.createObjectURL(invoiceFile);
  }, [invoiceFile]);

  const leadGuest = guestData?.find(g => g.isLeadGuest);
  const leadGuestName = [leadGuest?.title, leadGuest?.firstName, leadGuest?.lastName].filter(Boolean).join(' ') || null;

  const exchangeRate = breakdownData?.headlineLineItemBreakdown?.ExchangeRate;
  const titleMap = breakdownData
    ? getAllBreakdownItemTitles(breakdownData.headlineLineItemBreakdown)
    : new Map<string, string>();

  const items: IPurchaseOrderSearchLineItem[] = po?.items ?? [];

  const totalBookingCents = items.reduce((sum, item) => sum + item.costCents, 0);

  const getSourceCost = (costCents: number): string => {
    if (!exchangeRate?.rate) {
      return `${po?.currencySymbol ?? ''} ${formatAmount(costCents / 100)}`;
    }
    return `${String(exchangeRate.currency)} ${formatAmount(costCents / 100 / exchangeRate.rate)}`;
  };

  const totalSourceDisplay = !exchangeRate?.rate
    ? `${po?.currencySymbol ?? ''} ${formatAmount(totalBookingCents / 100)}`
    : `${String(exchangeRate.currency)} ${formatAmount(totalBookingCents / 100 / exchangeRate.rate)}`;

  const poBookingCurrency = (po?.currencyCode ?? 'USD') as TCurrencyCode;

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const handleInvoiceFileSelected = (file: File) => {
    setInvoiceFile(file);
    setIsUploadModalOpen(false);
  };

  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true);
  };

  if (poError) {
    return (
      <div className="w-auto mx-[20px] crm-desktop:mx-[80px]">
        <ErrorBar />
      </div>
    );
  }

  return (
    <div className="w-auto mx-[20px] crm-desktop:mx-[80px]">
      <LeaveWithoutSavingModal when={invoiceFile !== null} />
      {isLoading && <LoadingBar />}

      {!isLoading && po && (
        <div className="grid crm-desktop:grid-cols-[1fr_400px] gap-[20px]">
          {/* Left panel — page body */}
          <div>
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <Link
                  to="/finance/suppliers-reconciliation"
                  className="font-pt-sans text-[13px] text-brown-100 underline block mb-2"
                >
                  Back
                </Link>
                <span className="font-noe-display text-[28px] font-normal block">Reconciliation</span>
              </div>
              <div className="text-right font-pt-sans text-[13px] text-gray-100">
                <div>Created {formatDateTime(po.createdAt)}</div>
                <div>Modified {formatDateTime(po.updatedAt)}</div>
              </div>
            </div>

            {/* Purchase Order section */}
            <AccordionStackable
              summary={
                <span className="font-pt-sans text-[14px] text-black font-bold uppercase">
                  Purchase Order
                </span>
              }
              isOpen={isPurchaseOrderOpen}
              onToggle={setIsPurchaseOrderOpen}
              wrapperClassName="!border-0"
              buttonClassName="!px-0 !py-3"
              contentWrapperClassName="!px-0 !py-0"
            >
              <BorderWithLabel label="Details">
                <PurchaseOrderInfoPanel
                  po={po}
                  bookingUuid={bookingUuid}
                  leadGuestName={leadGuestName}
                />
              </BorderWithLabel>

              <PurchaseOrderItemsTable
                items={items}
                currencySymbol={po.currencySymbol}
                titleMap={titleMap}
                getSourceCost={getSourceCost}
                totalBookingCents={totalBookingCents}
                totalSourceDisplay={totalSourceDisplay}
                dueDates={po.dueDates ?? []}
                exchangeRate={exchangeRate}
              />
            </AccordionStackable>

            {/* Invoice section */}
            <InvoiceSection
              file={invoiceFile}
              onOpenUploadModal={handleOpenUploadModal}
              poCurrencySymbol={po.currencySymbol}
              poBookingCurrency={poBookingCurrency}
              poBookingTotalCents={totalBookingCents}
              poDueDates={po.dueDates ?? []}
              exchangeRate={exchangeRate}
              bookingUuid={bookingUuid}
              purchaseOrderUuid={po.uuid}
              supplierCode={po.supplierCode as string | null}
            />
          </div>

          {/* Right panel — invoice file preview */}
          <InvoicePreviewPanel
            file={invoiceFile}
            filePreviewUrl={invoiceFilePreviewUrl}
            onReplace={handleOpenUploadModal}
          />
        </div>
      )}

      {isUploadModalOpen && (
        <UploadInvoiceModal
          onClose={() => setIsUploadModalOpen(false)}
          onFileSelected={handleInvoiceFileSelected}
        />
      )}
    </div>
  );
};
